import {encrypt, decrypt} from './encrypt';
import {isEmpty, isNil, isBoolean} from 'lodash/lang';
import {get} from 'lodash/object';
import {COOKIE_PREFIX, DOMAIN} from 'configs';

function optionsToString(options = {}) {
  let optionsString = '';

  // Receive days and convert it to expires
  if (options.exdays) {
    const milliseconds = options.exdays * 24 * 60 * 60 * 1000;

    let newDate = new Date();
    newDate.setTime(newDate.getTime() + milliseconds); // Day
    optionsString += '; expires=' + newDate.toUTCString();
  }

  if (options.expires) {
    optionsString += '; expires=' + options.expires.toUTCString();
  }

  if (options.maxAge) {
    // 60*60*24*365 or 31536000 for a year
    // 60*60*24 for a day
    // 60*60 for a hour
    // 60 for a minute
    optionsString += '; max-age=' + options.maxAge;
  }

  if (options.domain) {
    optionsString += '; domain=' + options.domain;
  }

  if (options.path) {
    optionsString += '; path=' + options.path;
  }

  if (options.secure) {
    optionsString += '; secure';
  }

  if (options.httpOnly) {
    optionsString += '; httponly';
  }

  if (options.sameSite) {
    optionsString += '; samesite=' + options.sameSite;
  }

  return optionsString;
}

export const getCookie = (cname) => {
  let name = cname + '=';
  let decodedCookie = decodeURIComponent(document.cookie);

  let ca = decodedCookie.split(';');

  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];

    while (c.charAt(0) === ' ') {
      c = c.substring(1);
    }

    if (c.indexOf(name) === 0) {
      return c.substring(name.length, c.length);
    }
  }

  return '';
};

const setCookie = (cname, cvalue = '', options = {}) => {
  const cookie = [cname, cvalue].join('=');

  const optionsString = optionsToString(options);
  document.cookie = [cookie, optionsString].join('');
};

const getDecryptedCookie = (name) => {
  const value = getCookie(`${COOKIE_PREFIX}_${name}`);
  const decrypted = decrypt(value);

  // this if statement will true if the secret cookies is changed
  if (!!value && isNil(decrypted)) {
    // This will delete the old cookies if the domain is default
    setCookie(`${COOKIE_PREFIX}_${name}`, '');
    // this will delete the new cookies if the domain is specified already
    setCookie(`${COOKIE_PREFIX}_${name}`, '', {domain: DOMAIN});
    return undefined;
  }

  return decrypted;
};

const setEncryptedCookie = (name, value = '', options = {}) => {
  if (!isEmpty(value) || isBoolean(value)) {
    value = encrypt(value);
  } else {
    // Delete cookie if it is exist.
    value = '';
    options.exdays = -1;
    options.expires = 0;
  }

  options.secure = true;
  options.path = '/';
  options.domain = DOMAIN;

  setCookie(`${COOKIE_PREFIX}_${name}`, value, options);
};

/**
 * Frontend on different subdomains
 * SameSite=None; Secure; Domain=.yourdomain.com; HttpOnly
 */

const Cookies = {
  get session() {
    return getDecryptedCookie('ssid');
  },
  set session(value) {
    const remember = get(value, 'remember');
    const isTrue = !isEmpty(remember) || remember === true;

    const options = {
      exdays: isTrue ? 1 : '',
      sameSite: 'none',
    };

    setEncryptedCookie('ssid', value, options);
  },
  get csrfToken() {
    return getDecryptedCookie('csrf-token');
  },
  set csrfToken(value) {
    const options = {
      exdays: 1,
      secure: true,
      httpOnly: false,
      sameSite: 'none',
    };

    setEncryptedCookie('csrf-token', value, options);
  },
  get bsid() {
    return getDecryptedCookie('bsid');
  },
  set bsid(value) {
    const options = {
      exdays: 365,
      sameSite: 'none',
      secure: true,
    };
    setEncryptedCookie('bsid', value, options);
  },
  get captchaToken() {
    return getDecryptedCookie('ctoken');
  },
  set captchaToken(value) {
    setEncryptedCookie('ctoken', value);
  },
  get captchaType() {
    return getDecryptedCookie('ctype');
  },
  set captchaType(value) {
    setEncryptedCookie('ctype', value);
  },

  get consent() {
    return getDecryptedCookie('consent');
  },
  set consent(value) {
    // changes consent after 30 days from 365 days
    const options = {exdays: 30};
    setEncryptedCookie('consent', value, options);
  },

  get emailSession() {
    return getDecryptedCookie('esid');
  },
  set emailSession(value) {
    setEncryptedCookie('esid', value);
  },

  get dontShowProgress() {
    return getDecryptedCookie('dsp-flag');
  },
  set dontShowProgress(value) {
    const options = {exdays: 30};
    setEncryptedCookie('dsp-flag', value, options);
  },
};

export default Cookies;
